import http from 'common/http'
import { isEqual } from 'es-toolkit/compat'
import {
  legendCreated,
  legendsFetched,
  legendDeleted,
  updateCropLegend,
} from 'actions/legend'
import { closeModal } from 'actions/app'
import { updateLayer } from 'actions/map'

/*
 * The loaded harvests a crop legend applies to. A legend with a cropYear covers
 * that season only; a null cropYear still means every year.
 *
 * Everything scoping this legend to the right layers derives from this list, so
 * it is computed once and handed to both reducers rather than each of them
 * guessing.
 */
const scopedHarvestIds = (getState, legendData): number[] => {
  const state = getState()
  const harvests = state.harvest.collection || []
  const seasonSpecificYears = new Set(
    (state.field.cropLegends || [])
      .filter(
        (legend) =>
          legend.cropId === legendData?.cropId &&
          legend.parameter === legendData?.parameter &&
          legend.cropYear != null,
      )
      .map((legend) => legend.cropYear),
  )
  return harvests
    .filter(
      (harvest) =>
        harvest.cropId === legendData?.cropId &&
        (legendData?.cropYear == null
          ? !seasonSpecificYears.has(harvest.cropYear)
          : harvest.cropYear === legendData.cropYear),
    )
    .map((harvest) => harvest.id)
}

const syncLegendLayers = (dispatch, getState, legendData) => {
  const state = getState()
  const layerInfo = state.field.layerInfo || []
  const wmsLayers = state.map.wmsLayers || []

  if (legendData?.parameter === 'dem') {
    const isGlobalLegend =
      legendData.harvestId == null && legendData.cropId == null

    if (isGlobalLegend) {
      wmsLayers.forEach((wmsLayer) => {
        if (wmsLayer.name === 'dem') {
          const layerInfoEntry = layerInfo.find(
            (l) => l.layerName === 'dem' && l.harvestId === wmsLayer.harvestId,
          )
          if (
            layerInfoEntry &&
            !isEqual(wmsLayer.customLegends, layerInfoEntry.customLegends)
          ) {
            dispatch(
              updateLayer({
                ...layerInfoEntry,
                customLegends: layerInfoEntry.customLegends,
              }),
            )
          }
        }
      })
    } else {
      layerInfo.forEach((layer) => {
        if (layer.layerName === 'dem') {
          const wmsLayer = wmsLayers.find(
            (w) => w.name === 'dem' && w.harvestId === layer.harvestId,
          )
          if (
            wmsLayer &&
            !isEqual(wmsLayer.customLegends, layer.customLegends)
          ) {
            dispatch(
              updateLayer({ ...layer, customLegends: layer.customLegends }),
            )
          }
        }
      })
    }
  } else if (legendData?.cropId) {
    const harvestIds = scopedHarvestIds(getState, legendData)

    dispatch(updateCropLegend(legendData, harvestIds))

    layerInfo.forEach((layer) => {
      if (
        (layer.layerTitle === 'corrected_yield' ||
          layer.layerName === 'swath') &&
        harvestIds.includes(layer.harvestId)
      ) {
        const wmsLayer = wmsLayers.find(
          (w) => w.name === layer.layerName && w.harvestId === layer.harvestId,
        )
        if (wmsLayer && !isEqual(wmsLayer.customLegends, layer.customLegends)) {
          dispatch(
            updateLayer({ ...layer, customLegends: layer.customLegends }),
          )
        }
      }
    })
  }
}

export const createLegend = (legendData) => {
  return (dispatch, getState) => {
    return http.post(`/legend`, legendData).then((res) => {
      dispatch(legendCreated(res.data, scopedHarvestIds(getState, res.data)))
      syncLegendLayers(dispatch, getState, res.data)
      return 'LegendSet'
    })
  }
}

export const updateLegend = (legendId, legendData) => {
  return (dispatch, getState) => {
    return http.put(`/legend/${legendId}`, legendData).then((res) => {
      dispatch(legendCreated(res.data, scopedHarvestIds(getState, res.data)))
      syncLegendLayers(dispatch, getState, res.data)
      return 'LegendSet'
    })
  }
}

/*
 * Asks the API to derive crop legends from the season's pooled harvest
 * histograms. Returns proposals only — nothing is saved, and no colours are
 * included: the caller composes ranges.c from the palettes and saves through
 * createLegend/updateLegend.
 *
 * The global error modal is suppressed because the generator dialog stays open
 * on failure: this endpoint answers a rejected request with { reason, code } so
 * the caller can explain which input to fix, which is no use if the alert has
 * already replaced the dialog holding those inputs.
 */
export const generateCropLegends = (params) => {
  return () => {
    return http
      .post('/legend/crop-legends/generate', params, {
        suppressErrorModal: true,
      })
      .then((res) => res.data)
  }
}

export const getCropLegends = () => {
  return (dispatch) => {
    return http.get('/legend/crop-legends').then((res) => {
      dispatch(legendsFetched(res.data))
      return res.data
    })
  }
}

export const getGlobalDemLegend = () => {
  return (dispatch) => {
    return http.get('/legend').then((res) => {
      // Filter for global DEM legend: parameter === 'dem', cropId === null, harvestId === null
      const globalDemLegend = res.data?.find(
        (legend) =>
          legend.parameter === 'dem' &&
          legend.cropId === null &&
          legend.harvestId === null,
      )
      return globalDemLegend || null
    })
  }
}

/*
 * `options.suppressErrorModal` is for callers that delete several legends and
 * report the failures themselves — the global alert would replace their dialog
 * mid-batch, hiding which legends are left.
 */
export const deleteLegend = (legendId, layer, swathParameter, options = {}) => {
  return (dispatch) => {
    return http
      .delete(`/legend/${legendId}`, {
        suppressErrorModal: options?.['suppressErrorModal'] === true,
      })
      .then((res) => {
        if (layer?.name === 'swath' && swathParameter) {
          const meta = { type: 'layer', layer, swathParameter }
          dispatch(legendDeleted(legendId, meta))
        } else if (layer) {
          const meta = { type: 'layer', layer }
          dispatch(legendDeleted(legendId, meta))
        } else {
          dispatch(legendDeleted(legendId, {}))
        }
        // NOTE: Callers are responsible for closing the modal after deletion.
        // This allows components to perform custom syncing (e.g., fetching fresh layer data)
        // before unmounting. Use .then(() => dispatch(closeModal())) in the calling component.
        return res
      })
  }
}
